import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "../../components/MobileLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { formatIndianCurrency } from "../../utils/formatters";
import {
  Wallet,
  TrendingUp,
  Users,
  AlertCircle,
  ArrowUpRight,
  Share2,
  BookPlus,
  Eye,
  Download,
} from "lucide-react";

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const walletRes = await api.get("/wallets/me");
      console.log("Wallet Data:", walletRes.data);
      setWallet(walletRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const totalBalance = useMemo(() => {
    const capital = Number(wallet?.capitalBalance || 0);
    const earnings = Number(wallet?.earningsBalance || 0);
    const networking = Number(wallet?.networkBalance || 0);
    return capital + earnings + networking;
  }, [wallet]);

  const handleWhatsAppShare = () => {
    const referralCode = user?.referralCode || "N/A";
    const appUrl = `${window.location.origin}/tikhat.apk`;

    const message = `🎉 Join Tikhat Foods Production Partner Program!

Use my referral code: *${referralCode}* to get network benefits and start earning.

📲 Download the Partner App: ${appUrl}

Start your production cycle today and grow your income! 💰`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="p-4 space-y-4 animate-pulse bg-gray-50 min-h-full">
          <div className="h-24 rounded-2xl bg-white" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-24 rounded-xl bg-white" />
            <div className="h-24 rounded-xl bg-white" />
          </div>
          <div className="h-24 rounded-xl bg-white" />
          <div className="h-32 rounded-xl bg-white" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="min-h-full bg-gray-50">
        <div className="p-4 pb-24 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Welcome back</p>
              <h1 className="text-xl font-semibold tracking-tight text-gray-900">
                {user?.name || "Investor"}
              </h1>
              <p className="mt-1 text-[11px] text-gray-500">
                Overview of your balances and plan
              </p>
            </div>
            <button
              onClick={() => navigate("/profile")}
              className="flex items-center gap-2 rounded-full bg-white px-3 py-1 border border-gray-200 text-xs text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                {user?.name ? user.name[0].toUpperCase() : "U"}
              </div>
              <span>Profile</span>
            </button>
          </div>

          {/* Referral code */}
          <div className="flex items-center justify-between rounded-xl bg-white border border-gray-200 px-3 py-2 shadow-sm">
            <div className="flex flex-col">
              <span className="text-[11px] text-gray-500">Referral code</span>
              <span className="font-mono text-sm font-semibold tracking-wide text-gray-900">
                {user?.referralCode || "N/A"}
              </span>
            </div>
            <button
              onClick={() => navigate("/network")}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
            >
              <Share2 className="h-3.5 w-3.5" />
              Invite & Earn
            </button>
          </div>

          {/* KYC status */}
          {/* {user?.kycStatus !== "Approved" && (
            <div className="rounded-2xl bg-white border border-amber-200 px-3 py-3 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-amber-50">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-900">
                    Complete KYC to unlock full access
                  </p>
                  <p className="mt-1 text-[11px] text-gray-600">
                    KYC is required for deposits, withdrawals and commissions.
                  </p>
                  <button
                    onClick={() => navigate("/kyc")}
                    className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-[11px] font-semibold text-white hover:bg-amber-600"
                  >
                    Complete KYC
                    <ArrowUpRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          )} */}

          {/* Total balance card */}
          <div className="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-gray-500">Total Wallet Value</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-gray-900">
                  {formatIndianCurrency(totalBalance)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 text-[11px] text-gray-600">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5">
                  <TrendingUp className="h-3 w-3 text-emerald-500" />
                  Updated
                </span>
                <span className="text-[11px] text-gray-500">
                  {wallet?.planName || "No active plan"}
                </span>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded-xl bg-gray-50 px-2.5 py-1.5">
                <div className="flex items-center gap-1 text-gray-600">
                  <Wallet className="h-3 w-3 text-indigo-500" />
                  <span>Production Fund</span>
                </div>
                <p className="mt-1 font-mono text-xs font-semibold text-gray-900">
                  {formatIndianCurrency(wallet?.capitalBalance || 0)}
                </p>
              </div>
              <div className="rounded-xl bg-gray-50 px-2.5 py-1.5">
                <div className="flex items-center gap-1 text-gray-600">
                  <TrendingUp className="h-3 w-3 text-emerald-500" />
                  <span>Revenue</span>
                </div>
                <p className="mt-1 font-mono text-xs font-semibold text-gray-900">
                  {formatIndianCurrency(wallet?.earningsBalance || 0)}
                </p>
              </div>
              <div className="rounded-xl bg-gray-50 px-2.5 py-1.5">
                <div className="flex items-center gap-1 text-gray-600">
                  <Users className="h-3 w-3 text-sky-500" />
                  <span>Network</span>
                </div>
                <p className="mt-1 font-mono text-xs font-semibold text-gray-900">
                  {formatIndianCurrency(wallet?.networkBalance || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-3">
            {/* 🟢 Start Production Cycle */}
            <button
              onClick={() => navigate("/deposits")}
              className="rounded-2xl bg-green-600 text-white flex flex-col items-center justify-center gap-2 py-3 shadow-sm hover:bg-green-700 active:scale-[0.98] transition"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                <BookPlus className="h-4 w-4" />
              </div>
              <span className="text-[11px] font-semibold">
                Start Production Cycle
              </span>
            </button>

            {/* 🔵 Withdraw Profit */}
            <button
              onClick={() => navigate("/withdrawals")}
              className="rounded-2xl bg-blue-600 text-white flex flex-col items-center justify-center gap-2 py-3 shadow-sm hover:bg-blue-700 active:scale-[0.98] transition"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                <ArrowUpRight className="h-4 w-4" />
              </div>
              <span className="text-[11px] font-semibold">Withdraw Profit</span>
            </button>

            {/* 🟠 Share Partner App - Mobile Only */}
            <button
              onClick={() => {
                const referralCode = user?.referralCode || "";
                const apkUrl = `${window.location.origin}/tikhat.apk`;

                const message =
                  `Join Tikhat Production Partner Program!\n\n` +
                  `Use my referral code: ${referralCode}\n\n` +
                  `Use referral code to get network benefits!\n\n` +
                  `• Start production cycle\n` +
                  `• Earn revenue sharing\n` +
                  `• Build your network\n` +
                  `• Monthly withdrawals\n\n` +
                  `Download App: ${apkUrl}`;

                // Check if mobile device
                const isMobile =
                  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                    navigator.userAgent
                  );

                if (isMobile) {
                  // Use WhatsApp with download link
                  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
                    message
                  )}`;
                  window.open(whatsappUrl, "_blank");
                } else {
                  // On desktop, just download the APK
                  window.location.href = "/tikhat.apk";
                }
              }}
              className="rounded-2xl bg-orange-500 text-white flex flex-col items-center justify-center gap-2 py-3 shadow-sm hover:bg-orange-600 active:scale-[0.98] transition"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                <Share2 className="h-4 w-4 md:hidden" />
                <Download className="h-4 w-4 hidden md:block" />
              </div>

              <span className="text-[11px] font-semibold md:hidden">
                Share Partner App
              </span>
              <span className="text-[11px] font-semibold hidden md:block">
                Download Partner App
              </span>
            </button>
          </div>

          {/* Plan section */}
          <div className="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Plan & Withdrawal
                </h3>
              </div>
              {wallet?.planName && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 border border-emerald-100">
                  Active
                </span>
              )}
            </div>

            <div className="space-y-2 text-[12px]">
              <div className="flex justify-between">
                <span className="text-gray-500">Current Plan</span>
                <span className="font-medium text-gray-900">
                  {wallet?.planName || "No plan active"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Next Withdrawal Cycle</span>
                <span className="font-medium text-gray-900">
                  10th of each month
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Lock-in Status</span>
                <span className="font-medium text-emerald-600">
                  {wallet?.lockInStatus || "As per plan rules"}
                </span>
              </div>
            </div>
          </div>

          {/* Network overview */}
          <div className="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm mb-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-sky-500" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Network Overview
                </h3>
              </div>
              <span className="text-[10px] text-gray-500">
                Referral snapshot
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl bg-gray-50 px-2 py-2">
                <p className="text-lg font-semibold text-gray-900">0</p>
                <p className="mt-0.5 text-[11px] text-gray-500">Direct</p>
              </div>
              <div className="rounded-xl bg-gray-50 px-2 py-2">
                <p className="text-lg font-semibold text-gray-900">0</p>
                <p className="mt-0.5 text-[11px] text-gray-500">Total Team</p>
              </div>
              <div className="rounded-xl bg-gray-50 px-2 py-2">
                <p className="text-lg font-semibold text-gray-900">
                  {formatIndianCurrency(0)}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">Commission</p>
              </div>
            </div>

            {/* <button
              onClick={() => navigate('/network')}
              className="mt-3 w-full rounded-xl bg-gray-900 px-3 py-2 text-[12px] font-semibold text-white hover:bg-black"
            >
              View Network Tree
            </button> */}
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Home;
